import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { useLanguage } from '../i18n/LanguageProvider';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Modal from '../components/Modal';

const Tools = ({ clinic, onLogout }) => {
  const { t } = useLanguage();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Modal states
  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    confirmText: 'OK',
    showCancel: false
  });


  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/api/assistant/tools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTools(response.data.tools || []);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        onLogout();
        return;
      }
      setMessage(error.response?.data?.error || t('tools.failedToFetchTools'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteTool = (toolId) => {
    setModal({
      isOpen: true,
      title: t('tools.confirmDelete'),
      message: t('tools.confirmDeleteMessage'),
      type: 'warning',
      onConfirm: () => deleteTool(toolId),
      confirmText: t('common.delete'),
      showCancel: true
    });
  };

  const deleteTool = async (toolId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(`${API_BASE_URL}/api/assistant/tools/${toolId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage(t('tools.toolDeleted'));
      setMessageType('success');
      fetchTools();
    } catch (error) {
      setMessage(error.response?.data?.error || t('tools.failedToDeleteTool'));
      setMessageType('error');
    }
  };

  const handleAddEmailTool = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_BASE_URL}/api/assistant/tools`, 
        {}, // No body needed since tool ID is hardcoded in backend
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage(t('tools.emailToolAdded'));
      setMessageType('success');
      fetchTools();
    } catch (error) {
      setMessage(error.response?.data?.error || t('tools.failedToAddEmailTool'));
      setMessageType('error');
    }
  };


  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-semibold text-gray-900 hover:text-gray-700">
                {t('nav.clinicAssistant')}
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              <span className="text-gray-700">{clinic?.clinic_name}</span>
              <button
                onClick={onLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700"
              >
                {t('common.logout')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <Link
              to="/dashboard"
              className="text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('tools.backToDashboard')}
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {t('tools.manageTools')}
              </h2>
              <button
                onClick={handleAddEmailTool}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {t('tools.addEmailTool')}
              </button>
            </div>

            {message && (
              <div className={`mb-4 p-4 rounded-md ${
                messageType === 'success'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {message}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-lg text-gray-600">{t('tools.loadingTools')}</div>
              </div>
            ) : tools.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg mb-4">{t('tools.noTools')}</div>
                <p className="text-gray-400">{t('tools.noToolsDescription')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tools.map((toolId, index) => (
                  <div key={toolId || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {toolId === '58f2b9fd-876c-46bf-9349-ca0daf573eba' ? 'Email Notification Tool' : t('tools.unnamedTool')}
                        </h3>
                        <p className="text-gray-600 mt-1">
                          {toolId === '58f2b9fd-876c-46bf-9349-ca0daf573eba' ? 'Send email notifications when someone contacts the assistant' : t('tools.noDescription')}
                        </p>
                        <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                          <span className="bg-gray-100 px-2 py-1 rounded">
                            {toolId === '58f2b9fd-876c-46bf-9349-ca0daf573eba' ? 'Email Tool' : 'Tool'}
                          </span>
                          <span className="bg-blue-100 px-2 py-1 rounded text-xs font-mono">
                            {toolId.substring(0, 8)}...
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleDeleteTool(toolId)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Confirmation Modal */}
      <Modal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onClose={() => setModal(prev => ({ ...prev, isOpen: false }))}
        confirmText={modal.confirmText}
        showCancel={modal.showCancel}
      />
    </div>
  );
};

export default Tools;
